import {
  AIProfilesError,
  buildOpenRouterModelId,
  isKnownProfileInput,
  resolveInput,
} from "@x12i/ai-profiles";
import type {
  AIProfileBackend,
  AIModelPricing,
  InstructionTier,
  ProfileCatalogLane,
  RegistrySourceMode,
  ResolvedAIProfile,
} from "@x12i/ai-profiles";
import { TEXT_CATALOG_LANE } from "../profiles/catalogLane.js";
import { normalizeProvider, normalizeString } from "../sync/modelNameResolver/normalize.js";

export type AiProfilesModelMatch = {
  provider: string;
  modelId: string;
  canonicalModelId: string;
  pricing?: AIModelPricing;
  instructionTier: InstructionTier;
  backend: AIProfileBackend;
  matchedVia: "profile" | "model-id";
  profile?: string;
  choice?: string;
};

export type MatchModelInAiProfilesOptions = {
  source?: RegistrySourceMode;
  /** OpenRouter catalog lane (required by @x12i/ai-profiles). Defaults to {@link TEXT_CATALOG_LANE}. */
  catalogLane?: ProfileCatalogLane;
};

/** When set (tests), overrides default `auto` registry/catalog source. */
let registrySourceOverride: RegistrySourceMode | undefined;

function registrySource(options?: MatchModelInAiProfilesOptions): RegistrySourceMode {
  return options?.source ?? registrySourceOverride ?? "auto";
}

function catalogLane(options?: MatchModelInAiProfilesOptions): ProfileCatalogLane {
  return options?.catalogLane ?? TEXT_CATALOG_LANE;
}

/** ai-profiles returns a valid in-memory cache before honoring `source`; refresh on explicit sources. */
function resolveInputOptions(
  source: RegistrySourceMode,
  lane: ProfileCatalogLane,
) {
  const base = { source, catalogLane: lane };
  return source === "auto" ? base : { ...base, refresh: true as const };
}

function isLowConfidenceModelResolution(resolved: ResolvedAIProfile): boolean {
  return (
    resolved.resolutionKind === "model" &&
    resolved.warnings?.some((w) => w.code === "MODEL_INPUT_FALLBACK_GUESS") === true
  );
}

function resolvedToMatch(resolved: ResolvedAIProfile): AiProfilesModelMatch {
  const direct = resolved.invocation.direct;
  const canonical =
    resolved.invocation.openrouter?.modelId ??
    buildOpenRouterModelId(direct.provider, direct.modelId);
  return {
    provider: normalizeProvider(direct.provider) ?? direct.provider,
    modelId: normalizeString(direct.modelId),
    canonicalModelId: canonical,
    pricing: resolved.pricing,
    instructionTier: resolved.instructionTier,
    backend: resolved.backend,
    matchedVia: resolved.resolutionKind === "model" ? "model-id" : "profile",
    profile: resolved.profile,
    choice: resolved.choice,
  };
}

/**
 * Resolve via `@x12i/ai-profiles` {@link resolveInput} (profile-first, remote-capable).
 *
 * `provider` is only used for concrete model ids (`{ provider, modelId }`).
 * Known profile inputs are always passed as the bare string so a transport
 * hint like `openrouter` cannot turn `cheap/default` into a fake SKU.
 */
export async function matchModelInAiProfiles(
  model: string,
  provider?: string,
  options?: MatchModelInAiProfilesOptions,
): Promise<AiProfilesModelMatch | null> {
  const source = registrySource(options);
  const lane = catalogLane(options);
  const trimmed = model.trim();
  const input =
    provider && provider.trim() && !isKnownProfileInput(trimmed)
      ? { provider: provider.trim(), modelId: trimmed }
      : trimmed;

  try {
    const resolved = await resolveInput(input, resolveInputOptions(source, lane));
    if (isLowConfidenceModelResolution(resolved)) return null;
    return resolvedToMatch(resolved);
  } catch (err) {
    if (!(err instanceof AIProfilesError)) throw err;
    return null;
  }
}

/** @internal Test-only — reset module cache between tests. */
export function resetAiProfilesMatchCacheForTests(
  options?: { source?: RegistrySourceMode },
): void {
  registrySourceOverride = options?.source ?? "bundled";
}
